import { CdkTableModule } from '@angular/cdk/table';
import { Component, computed, inject, linkedSignal, signal, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { ActivatedRoute } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { lastValueFrom, map } from 'rxjs';
import { AssignmentWithRelations } from '../../../api/app-types/assignment';
import { Career } from '../../../api/app-types/career.app';
import { DB_PHASES, PHASE_TRANSITION_TYPES } from '../../../api/app-types/phase.app';
import { REPORT_TYPE, ReportDocument } from '../../../api/app-types/report-document.app';
import { ValidationPhaseResponse } from '../../../api/app-types/validation.app';
import { ASSIGNMENT_API, provideAssignmentApi } from '../../../api/services/assignment.service';
import { provideReportApi } from '../../../api/services/report.service';
import { AssignmentChecks } from '../../../core/components/assignment-checks/assignment-checks';
import { AssignmentDetails } from '../../../core/components/assignment-details/assignment-details';
import { AssignmentLinks } from '../../../core/components/assignment-links/assignment-links';
import { LvrDefaultActions } from '../../../core/components/default-actions/default.actions';
import { STATE } from '../../../core/services/state.service';
import { determinePhaseTransitionType } from '../../../shared/helpers/assignment.func';
import { matDateProviders, oldestPossibleDateForPhaseChange } from '../../../shared/helpers/date.func';
import { hasError } from '../../../shared/helpers/has-error.func';
import { NOTIFICATION_SERVICE } from '../../../shared/notification/notification';
import { LvrPaddingDirective } from '../../../shared/padding/padding.directive';
import { PaginatedResponse } from '../../../shared/pagination/paginated-response';
import { FormPersistenceService } from '../../../shared/storage/form-persistence.service';
import { ImmediateErrorStateMatcher } from '../../../shared/validators/immediate-error-state-matcher';
import { MatDialog } from '@angular/material/dialog';
import { LvrSelectDisabilityKindDialog } from './select-disability-kind-dialog';
import { LvrRedirectNav } from '../../../core/classes/redirect-nav.class';
import { PersonType } from '../../../api/app-types/person-type.app';
import { DisabilityProof } from '../../../api/app-types/disability-proof.app';
import { GenericDialog } from '../../../core/components/generic-dialog/generic-dialog';
import { hasPermission } from '../../../shared/permissions/permission.func';
import { SessionManagerService } from '../../../shared/storage/session-manager.service';
import { createErrorNotification } from '../../../shared/notification/notification.factory';

/**
 * ATTENTION:
 * This component needs to be refactored with next Phase change user story.
 * It currently only supports phase change from 1 to 4. Later it should support others as well.
 */

@Component({
	selector: 'lvr-assignment-move',
	templateUrl: './assignment-move.html',
	imports: [
		TranslocoPipe,
		LvrPaddingDirective,
		ReactiveFormsModule,
		MatDatepickerModule,
		MatFormFieldModule,
		CdkTableModule,
		AssignmentDetails,
		AssignmentLinks,
		AssignmentChecks,
		LvrDefaultActions,
	],
	styleUrl: './assignment-move.scss',
	providers: [FormPersistenceService, provideAssignmentApi, provideReportApi, matDateProviders],
})
export class AssignmentMove extends LvrRedirectNav {
	public readonly hasError = hasError;
	public readonly DB_PHASES = DB_PHASES;
	public readonly matcher = new ImmediateErrorStateMatcher();
	public maxDate = new Date(); // today
	private readonly activeRoute = inject(ActivatedRoute);
	private readonly api = inject(ASSIGNMENT_API);
	private readonly ntfy = inject(NOTIFICATION_SERVICE);
	private readonly state = inject(STATE);
	private readonly draftService = inject(FormPersistenceService);
	private readonly dialog = inject(MatDialog);
	private readonly session = inject(SessionManagerService);
	private readonly transloco = inject(TranslocoService);
	public readonly currentUrl = this.router.url;
	private readonly data = this.resolveDataAsSignal<{
		assignment?: AssignmentWithRelations;
		validationErrors: ValidationPhaseResponse;
	}>('assignmentData');
	private readonly initiatorsPage = this.resolveDataAsSignal<PaginatedResponse<PersonType>>('initiatorsPage');
	public readonly initiators = computed(() => this.initiatorsPage()?.entities ?? []);
	private readonly careerPage = this.resolveDataAsSignal<PaginatedResponse<Career>>('careerPage');
	private readonly reports = this.resolveDataAsSignal<PaginatedResponse<ReportDocument>>('reports');
	public readonly disabilityReport = this.resolveDataAsSignal<DisabilityProof>('disabilityReport');
	public readonly startReport = computed(() =>
		(this.reports()?.entities ?? []).find((r) => r.categoryReport.id === REPORT_TYPE.START_REPORT),
	);
	public readonly endReport = computed(() =>
		(this.reports()?.entities ?? []).find((r) => r.categoryReport.id === REPORT_TYPE.END_REPORT),
	);
	private readonly hasDraftImpedimentReport = computed(() => {
		const reports = this.reports()?.entities ?? [];
		return reports.some((r) => r.categoryReport.id === REPORT_TYPE.IMPEDIMENT_REPORT && !r.isFinal);
	});
	private readonly canTransitionPhases = hasPermission(this.session.permissions, 'Phases.Transition');
	public readonly validationData = computed(() => this.data()?.validationErrors);
	public readonly hasValidationErrors = computed(() => {
		const hasError = Object.keys(this.validationData()!).length > 0;
		return this.validationData() && hasError;
	});
	public readonly assignment = computed(() => this.data()?.assignment);
	public readonly targetPhase = computed(() => Number(this.assignment()?.phaseChangeId ?? DB_PHASES.P4) as DB_PHASES);
	public readonly phaseTransitionType = computed(() => determinePhaseTransitionType(this.assignment()));
	public readonly currentInstitutionId = computed(() => {
		const sorted =
			this.careerPage()?.entities.sort((a, b) => b.startOfCareerStage.getTime() - a.startOfCareerStage.getTime()) ?? [];
		return sorted[0]?.workplacesJob?.institutionBranch?.id;
	});
	public readonly isCurrentInstitutionMain = computed(() => {
		const sorted =
			this.careerPage()?.entities.sort((a, b) => b.startOfCareerStage.getTime() - a.startOfCareerStage.getTime()) ?? [];
		return sorted[0]?.workplacesJob?.institutionBranch?.isMainInstitution;
	});
	public readonly mainInstitutionId = computed(() => {
		const sorted =
			this.careerPage()?.entities.sort((a, b) => b.startOfCareerStage.getTime() - a.startOfCareerStage.getTime()) ?? [];
		return sorted[0]?.workplacesJob?.institutionBranch?.institutionId;
	});
	public readonly currentCareerId = computed(() => {
		const sorted =
			this.careerPage()?.entities.sort((a, b) => b.startOfCareerStage.getTime() - a.startOfCareerStage.getTime()) ?? [];
		return sorted[0]?.id;
	});
	public readonly isPhase4Transition = computed(
		() =>
			this.phaseTransitionType() === PHASE_TRANSITION_TYPES.P0_TO_P4 ||
			this.phaseTransitionType() === PHASE_TRANSITION_TYPES.P1_TO_P4 ||
			this.phaseTransitionType() === PHASE_TRANSITION_TYPES.P2_TO_P4 ||
			this.phaseTransitionType() === PHASE_TRANSITION_TYPES.P3_TO_P4,
	);
	public readonly isPhase3Transition = computed(
		() =>
			this.phaseTransitionType() === PHASE_TRANSITION_TYPES.P1_TO_P3 ||
			this.phaseTransitionType() === PHASE_TRANSITION_TYPES.P2_TO_P3,
	);
	public readonly isP2OrP3Transition = computed(
		() =>
			this.phaseTransitionType() === PHASE_TRANSITION_TYPES.P1_TO_P3 ||
			this.phaseTransitionType() === PHASE_TRANSITION_TYPES.P1_TO_P2,
	);
	public readonly saveButtonKey = computed(() => {
		// P1 -> P2 or P3  => Anmelden
		if (this.isP2OrP3Transition()) return 'assignment.phase.save_register';

		// P4 => Abmelden
		if (this.targetPhase() === DB_PHASES.P4) return 'assignment.phase.save_cancel';

		// P2 -> P3 => Ummelden
		return 'assignment.phase.save_switch';
	});

	public readonly minDate = computed(() => {
		return oldestPossibleDateForPhaseChange(
			this.targetPhase(),
			this.assignment(),
			this.state.previousAssignmentBeforeCurrent(),
		);
	});
	public readonly validationErrors = computed(() => {
		// Its a dynamic object, so we map it to an array
		const errors = this.data()?.validationErrors;
		if (!errors) {
			return [];
		}
		const errs = Object.keys(errors)
			.map((key) => errors[key])
			.flat();

		return errs.filter((err) => err.severity.toLowerCase() === 'error');
	});
	public readonly clientId: Signal<number | string> = toSignal(
		this.activeRoute.data.pipe(map((data) => data['clientId'])),
	);
	private readonly assignmentId: Signal<number | string> = computed(() => this.data()!.assignment!.id);
	public readonly saving = signal<boolean>(false);
	public readonly disableContinueButton = computed(() => {
		if (!this.canTransitionPhases()) return true;
		const errors = this.validationErrors();
		const targetPhase = this.targetPhase();
		if (targetPhase === DB_PHASES.P4 && this.isPhase4Transition() && errors.length === 0) {
			// for phase 4 transitions, we only need to check date validity and saving state
			if (this.saving()) return true;
			return false;
		}
		if (this.saving() || errors.length > 0) return true;

		return false;
	});
	public readonly selectedDisabilityKindId = linkedSignal(() => this.assignment()?.careRelatedHandicapId);

	private async confirmImpedimentReportDeletion() {
		if (!this.hasDraftImpedimentReport()) return true;

		const confirmation = await lastValueFrom(
			this.dialog
				.open(GenericDialog, {
					data: {
						titleKey: 'assignment.impediment_report_delete_dialog.title',
						messageKey: 'assignment.impediment_report_delete_dialog.message',
						confirmKey: 'assignment.impediment_report_delete_dialog.confirm',
						cancelKey: 'assignment.impediment_report_delete_dialog.cancel',
						iconConfirm: 'arrow_forward',
					},
					maxWidth: '600px',
				})
				.afterClosed(),
		);

		return !!confirmation;
	}

	private async confirmClosePhaseChanger() {
		if (!(this.assignment()?.phaseId === DB_PHASES.P2 && this.assignment()?.phaseChangeId === DB_PHASES.P3))
			return true;

		const confirmation = await lastValueFrom(
			this.dialog
				.open(GenericDialog, {
					data: {
						titleKey: 'assignment.phase.change_cancel_dialog.title',
						messageKey: 'assignment.phase.change_cancel_dialog.message',
						confirmKey: 'assignment.phase.change_cancel_dialog.confirm',
						cancelKey: 'assignment.phase.change_cancel_dialog.cancel',
						iconConfirm: 'arrow_forward',
					},
					maxWidth: '600px',
				})
				.afterClosed(),
		);

		return !!confirmation;
	}

	public async save() {
		if (this.saving()) {
			return;
		}

		this.saving.set(true);
		try {
			const confirmed = await this.confirmImpedimentReportDeletion();

			if (!confirmed) {
				return;
			}

			const target = this.targetPhase() || DB_PHASES.P4;
			const disabilityKindId = this.selectedDisabilityKindId();

			if (disabilityKindId == null && target === DB_PHASES.P3) {
				const result = await lastValueFrom(
					this.dialog
						.open(LvrSelectDisabilityKindDialog, {
							width: '400px',
						})
						.afterClosed(),
				);

				if (result) {
					// Assignment edit
					await this.navigateAfterFinished(
						['/desk', 'client', this.clientId(), 'assignments', this.assignmentId(), 'edit'],
						{
							queryParams: { redirectUrl: this.router.url },
						},
					);
				}
				return;
			}
			await this.api.movePhase(this.clientId(), this.assignmentId(), target, this.selectedDisabilityKindId());
			await this.state.updateAssignments();
			await this.router.navigate(['../../'], { relativeTo: this.activeRoute });
			this.draftService.deleteDraft('phase', this.assignmentId()!.toString());
			this.ntfy.addNotification({
				type: 'success',
				message: 'Auftrag erfolgreich ' + (target === DB_PHASES.P4 ? 'abgeschlossen' : 'umgemeldet'),
				id: window.crypto.randomUUID(),
				showToast: true,
				timestamp: new Date(),
			});
		} catch (error) {
			/* eslint-disable @typescript-eslint/no-explicit-any */
			const errorMsg =
				(error as any)?.error?.errors?.[0] ??
				(error as any)?.error?.detail ??
				(error as any)?.error?.title ??
				'Fehler beim Ummelden des Auftrags';
			/* eslint-enable @typescript-eslint/no-explicit-any */
			this.ntfy.addNotification({
				type: 'error',
				message: errorMsg,
				id: window.crypto.randomUUID(),
				showToast: true,
				timestamp: new Date(),
			});
		} finally {
			this.saving.set(false);
		}
	}

	public async cancel() {
		const confirmed = await this.confirmClosePhaseChanger();

		if (!confirmed) {
			return;
		}

		try {
			if (
				confirmed &&
				this.assignment()?.phaseId === DB_PHASES.P2 &&
				this.assignment()?.phaseChangeId === DB_PHASES.P3
			) {
				await this.api.resetAssignment(this.assignment()!.clientId, this.assignment()!.id, DB_PHASES.P2);
			}

			// -1 is the indicator to tell the backend to cancel the assignment without reassigning to another phase
			await this.api.startPhaseTransition(this.clientId(), this.assignmentId(), -1);
			await this.state.updateAssignments();
			this.draftService.deleteDraft('phase', this.assignmentId()!.toString());
			await this.router.navigate(['../../'], { relativeTo: this.activeRoute });
		} catch {
			this.ntfy.addNotification(
				createErrorNotification(this.transloco.translate('base.notification_message.assignment_draft_delete_error')),
			);
		}
	}
}
